/**
 * Rl31RequestsPanel — file PMS demandes de Relevé 31.
 * Batch #14.154 — extracted from AdminPmsQueuePage.jsx during refactor.
 */
import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import {
  Receipt, CheckCircle2, Clock, Upload, X, ChevronRight, Loader2, Trash2,
  FileText, ExternalLink,
} from "lucide-react";
import { toast } from "@/lib/toast";
import { API, authH } from "./_shared";
import ArchiveButton from "@/components/admin/ArchiveButton";
import AdminTaskStatusSelector from "@/components/admin/AdminTaskStatusSelector";

function Rl31DetailModal({ item, onClose, onUpdated }) {
  const isCompleted = item.status === "completed";
  const isProcessing = item.status === "processing";
  const tenantsDetail = item.tenants_detail || [];
  const [note, setNote] = useState(
    isCompleted
      ? (item.completion_note || "")
      : "Bonjour, vos Relevés 31 ont été produits et déposés dans votre coffre-fort.",
  );
  const [busy, setBusy] = useState(false);
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  // Batch #14.178 — Génération PDF officielle depuis le panel admin
  const [generating, setGenerating] = useState(false);
  const [generated, setGenerated] = useState(
    item.rl31_pdf_safe_doc_id
      ? { safe_doc_id: item.rl31_pdf_safe_doc_id, drive_web_view_link: item.rl31_pdf_drive_web_view_link, year: item.metadata?.year }
      : null,
  );
  const year = item.metadata?.year || "—";

  const generateRl31Pdf = async () => {
    if (!window.confirm(
      `Générer le PDF officiel du Relevé 31 ${year} avec ${tenantsDetail.length} locataire(s) ?\n\n` +
      "Le PDF sera créé à partir des informations saisies par le client et stocké dans son coffre-fort, " +
      "MAIS le client ne sera PAS encore notifié. Vous pourrez vérifier le PDF avant de cliquer " +
      "« Marquer traitée et notifier »."
    )) return;
    setGenerating(true);
    try {
      const r = await axios.post(
        `${API}/admin/pms/rl31-requests/${item.id}/generate-pdf`,
        { notify_client: false },
        { headers: authH() },
      );
      toast.success("PDF généré et déposé dans le coffre · client non notifié");
      setGenerated(r.data);
      onUpdated();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur lors de la génération");
    } finally {
      setGenerating(false);
    }
  };

  // Batch #14.173 — Bouton « Accepter » : passe en processing, notifie client,
  // et apparaîtra ensuite dans Immeuble → tuile RL-31 pour génération
  const acceptRequest = async () => {
    if (!window.confirm(
      `Accepter cette demande RL-31 ${year} avec ${tenantsDetail.length} locataire(s) ?\n\n` +
      "Le client sera notifié que sa demande est en cours de traitement.\n" +
      "Vous générerez ensuite les PDFs et ferez la transmission via :\n" +
      "Immeuble → Master → Tuile « Relevé 31 »."
    )) return;
    setBusy(true);
    try {
      await axios.put(`${API}/admin/pms/rl31-requests/${item.id}/accept`, {}, { headers: authH() });
      toast.success("Demande acceptée · à générer dans Immeuble → tuile RL-31");
      onUpdated();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    } finally {
      setBusy(false);
    }
  };

  const markCompleted = async () => {
    setBusy(true);
    try {
      await axios.put(`${API}/admin/pms/rl31-requests/${item.id}/complete`, { note }, { headers: authH() });
      toast.success("Demande marquée traitée · client notifié");
      onUpdated();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    } finally {
      setBusy(false);
    }
  };

  const reopenRl31 = async () => {
    if (!window.confirm("Rouvrir cette demande comme étant à nouveau « en attente » ?")) return;
    setBusy(true);
    try {
      await axios.put(`${API}/admin/pms/rl31-requests/${item.id}/reopen`, {}, { headers: authH() });
      toast.success("Demande rouverte");
      onUpdated();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    } finally {
      setBusy(false);
    }
  };

  const uploadPdf = async () => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await axios.post(`${API}/admin/pms/rl31-requests/${item.id}/upload-pdf`, fd, {
        headers: { ...authH(), "Content-Type": "multipart/form-data" },
      });
      toast.success("PDF déposé dans le coffre-fort du client");
      setFile(null);
      onUpdated();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur upload");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" data-testid="admin-rl31-detail-modal">
      <div className="bg-white rounded-2xl shadow-2xl max-w-xl w-full max-h-[92vh] flex flex-col overflow-hidden">
        <div className="px-5 py-3 border-b flex items-center justify-between bg-amber-50">
          <div className="flex items-center gap-3">
            <Receipt className="w-5 h-5 text-amber-600" />
            <div>
              <h3 className="font-bold text-slate-800 text-sm">Demande Relevé 31 — {year}</h3>
              <p className="text-[10px] text-slate-500">Client : {item._master_name}</p>
            </div>
          </div>
          <button onClick={onClose} className="p-1.5 hover:bg-white rounded-lg"><X className="w-4 h-4" /></button>
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          <div className="rounded-xl border border-slate-200 bg-slate-50/50 p-3 text-[11px] space-y-1">
            <div><b>Soumis le :</b> {item.created_at?.slice(0, 10)}</div>
            {item.body && <div className="italic mt-1">« {item.body} »</div>}
            {isProcessing && (
              <div className="border-t pt-1 mt-1 text-amber-700">
                <b>⏳ Acceptée le :</b> {item.accepted_at?.slice(0, 10)} par {item.accepted_by || "—"}
                <div className="text-[10px] mt-0.5">À générer dans Immeuble → tuile RL-31</div>
              </div>
            )}
            {isCompleted && (
              <>
                <div className="border-t pt-1 mt-1"><b>Traitée le :</b> {item.completed_at?.slice(0, 10)} par {item.completed_by || "—"}</div>
                {item.completion_note && <div className="italic">Note : {item.completion_note}</div>}
              </>
            )}
          </div>

          {/* Batch #14.173 — Détail des locataires saisis par le proprio */}
          {tenantsDetail.length > 0 && (
            <div className="rounded-xl border border-amber-200 bg-amber-50/40 p-3" data-testid="admin-rl31-tenants-detail">
              <h4 className="text-xs font-semibold text-amber-800 uppercase tracking-wider mb-2">
                Locataires à déclarer ({tenantsDetail.length})
              </h4>
              <div className="space-y-2">
                {tenantsDetail.map((t, idx) => (
                  <div key={idx} className="rounded-lg bg-white border border-amber-200 p-2.5 text-[11px]">
                    <div className="flex items-center justify-between mb-1">
                      <span className="font-bold text-slate-800">#{idx + 1} · {t.name}</span>
                      <span className="font-mono text-amber-700">
                        {Number(t.total_rent_paid || 0).toLocaleString("fr-CA", { style: "currency", currency: "CAD" })}
                      </span>
                    </div>
                    {t.address && (
                      <div className="text-[10px] text-slate-600 italic mb-1">{t.address}</div>
                    )}
                    <div className="flex gap-3 flex-wrap text-[10px] text-slate-600">
                      <span>📅 {t.months_rented} mois loué{t.months_rented > 1 ? "s" : ""}</span>
                      {t.has_daycare_service && (
                        <span className="text-emerald-700">
                          ✓ Garderie incluse · {Number(t.daycare_amount || 0).toLocaleString("fr-CA", { style: "currency", currency: "CAD" })}
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {!isCompleted && (
            <div>
              <label className="block text-[10px] text-slate-500 mb-0.5 uppercase tracking-wider font-semibold">Note pour le client (envoyée par notification)</label>
              <textarea rows={3} value={note} onChange={(e) => setNote(e.target.value)} maxLength={1000} className="cc-input" data-testid="admin-rl31-note" />
            </div>
          )}

          <div className="rounded-xl border border-amber-200 bg-amber-50/30 p-3 space-y-2">
            <h4 className="text-xs font-semibold text-amber-800 uppercase tracking-wider">
              <FileText className="w-3 h-3 inline mr-1" />
              Étape 1 — Générer le PDF officiel
            </h4>
            <p className="text-[10px] text-slate-600">
              Crée le PDF Relevé 31 (format Revenu Québec) à partir des locataires saisis par le client,
              dépose dans le coffre-fort. <b>Le client n'est pas encore notifié</b> — vous pourrez vérifier
              le document avant de cliquer « Marquer traitée et notifier ».
            </p>
            {generated ? (
              <div className="rounded-lg bg-emerald-50 border border-emerald-200 p-2.5 space-y-1.5" data-testid="admin-rl31-generated-info">
                <div className="inline-flex items-center gap-1.5 text-emerald-700 font-semibold text-xs">
                  <CheckCircle2 className="w-3.5 h-3.5" />
                  PDF généré ✓
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {generated.drive_web_view_link && (
                    <a
                      href={generated.drive_web_view_link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-[11px] inline-flex items-center gap-1 px-2 py-1 rounded border border-emerald-300 text-emerald-700 hover:bg-emerald-100"
                      data-testid="admin-rl31-view-drive"
                    >
                      <ExternalLink className="w-3 h-3" />
                      Voir le PDF
                    </a>
                  )}
                  <button
                    onClick={generateRl31Pdf}
                    disabled={generating}
                    className="text-[11px] inline-flex items-center gap-1 px-2 py-1 rounded text-emerald-700 hover:bg-emerald-100"
                    data-testid="admin-rl31-regenerate-btn"
                  >
                    {generating ? <Loader2 className="w-3 h-3 animate-spin" /> : <FileText className="w-3 h-3" />}
                    Régénérer
                  </button>
                </div>
              </div>
            ) : (
              <button
                onClick={generateRl31Pdf}
                disabled={generating || tenantsDetail.length === 0}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-amber-600 text-white text-xs font-semibold hover:bg-amber-700 disabled:opacity-40"
                data-testid="admin-rl31-generate-btn"
              >
                {generating ? <Loader2 className="w-3 h-3 animate-spin" /> : <FileText className="w-3 h-3" />}
                Générer le PDF officiel
              </button>
            )}
          </div>

          <div className="rounded-xl border border-slate-200 bg-slate-50/40 p-3 space-y-2">
            <h4 className="text-xs font-semibold text-slate-700 uppercase tracking-wider">
              <Upload className="w-3 h-3 inline mr-1" />
              Alternative — Téléverser manuellement un PDF
            </h4>
            <p className="text-[10px] text-slate-600">Si vous avez généré le PDF dans un autre outil. Sera déposé dans le coffre-fort.</p>
            <div className="flex items-center gap-2 flex-wrap">
              <input type="file" accept=".pdf" onChange={(e) => setFile(e.target.files?.[0] || null)} className="cc-input flex-1" data-testid="admin-rl31-upload-file" />
              <button
                onClick={uploadPdf}
                disabled={!file || uploading}
                className="px-3 py-1.5 rounded-lg bg-slate-600 text-white text-xs font-semibold hover:bg-slate-700 disabled:opacity-40 inline-flex items-center gap-1.5"
                data-testid="admin-rl31-upload-submit"
              >
                {uploading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Upload className="w-3 h-3" />} Téléverser
              </button>
            </div>
          </div>
        </div>

        <div className="px-5 py-3 border-t bg-slate-50 flex justify-end gap-2 flex-wrap">
          <button onClick={onClose} className="px-3 py-1.5 text-sm rounded-lg border">Fermer</button>
          {!isCompleted && !isProcessing && (
            <button
              onClick={acceptRequest}
              disabled={busy}
              className="px-4 py-1.5 text-sm rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-40 inline-flex items-center gap-1.5"
              data-testid="admin-rl31-accept"
            >
              {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5" />}
              Accepter la demande
            </button>
          )}
          {isCompleted ? (
            <button onClick={reopenRl31} disabled={busy} className="px-4 py-1.5 text-sm rounded-lg border border-amber-300 text-amber-700 hover:bg-amber-50 inline-flex items-center gap-1.5" data-testid="admin-rl31-reopen">
              <Clock className="w-3.5 h-3.5" /> Rouvrir
            </button>
          ) : (
            <button onClick={markCompleted} disabled={busy} className="px-4 py-1.5 text-sm rounded-lg bg-amber-600 text-white hover:bg-amber-700 disabled:opacity-40 inline-flex items-center gap-1.5" data-testid="admin-rl31-complete">
              {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5" />}
              Marquer traitée et notifier
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default function Rl31RequestsPanel({ onUpdate }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("");
  const [selected, setSelected] = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  const fetchItems = useCallback(async () => {
    setLoading(true);
    try {
      const url = `${API}/admin/pms/rl31-requests${statusFilter ? `?status=${statusFilter}` : ""}`;
      const r = await axios.get(url, { headers: authH() });
      setItems(r.data?.items || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);
  useEffect(() => { fetchItems(); }, [fetchItems]);

  const handleDelete = async (n, e) => {
    e.stopPropagation();
    if (!window.confirm(`Supprimer définitivement la demande RL-31 ${n.metadata?.year || ""} ?\n\nCette action est irréversible.`)) return;
    setDeletingId(n.id);
    try {
      await axios.delete(`${API}/admin/pms/rl31-requests/${n.id}`, { headers: authH() });
      toast.success("Demande RL-31 supprimée");
      fetchItems();
      onUpdate?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Échec de suppression");
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <>
      <div className="px-5 py-3 border-b bg-slate-50 flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <Receipt className="w-4 h-4 text-amber-600" />
          <h2 className="font-semibold text-slate-800 text-sm">Demandes de Relevé 31</h2>
        </div>
        <div className="flex items-center gap-1">
          {[
            { v: "", label: "Toutes" },
            { v: "pending", label: "En attente" },
            { v: "completed", label: "Traitées" },
          ].map((s) => (
            <button
              key={s.v}
              onClick={() => setStatusFilter(s.v)}
              className={`text-[11px] px-2 py-0.5 rounded-full transition ${statusFilter === s.v ? "bg-amber-600 text-white" : "bg-white border border-slate-200 text-slate-600 hover:bg-slate-100"}`}
            >
              {s.label}
            </button>
          ))}
        </div>
      </div>
      <div className="p-3">
        {loading ? (
          <div className="text-center py-10"><Loader2 className="w-6 h-6 animate-spin text-slate-300 mx-auto" /></div>
        ) : items.length === 0 ? (
          <div className="text-center py-10 text-sm text-slate-400 italic">Aucune demande RL-31 {statusFilter || ""}</div>
        ) : (
          <div className="space-y-1.5">
            {items.map((n) => {
              const isCompleted = n.status === "completed";
              const year = n.metadata?.year || "—";
              return (
                <div
                  key={n.id}
                  className={`flex items-center gap-3 p-3 rounded-lg border transition cursor-pointer ${
                    isCompleted
                      ? "border-emerald-200 bg-emerald-50/30 hover:border-emerald-300"
                      : "border-amber-200 hover:border-amber-400 hover:bg-amber-50/30"
                  }`}
                  onClick={() => setSelected(n)}
                  data-testid={`admin-rl31-row-${n.id}`}
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-semibold text-slate-800 text-sm">RL-31 {year}</span>
                      {isCompleted ? (
                        <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-700 inline-flex items-center gap-1">
                          <CheckCircle2 className="w-2.5 h-2.5" /> Traitée
                        </span>
                      ) : (
                        <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700 inline-flex items-center gap-1">
                          <Clock className="w-2.5 h-2.5" /> En attente
                        </span>
                      )}
                    </div>
                    <div className="text-[11px] text-slate-500 truncate mt-0.5">
                      <b>{n._master_name}</b> · {n.created_at?.slice(0, 10)}
                      {n.body && <span className="italic"> · « {n.body.slice(0, 80)}{n.body.length > 80 ? "…" : ""} »</span>}
                    </div>
                    {isCompleted && n.completion_note && (
                      <div className="text-[10px] text-emerald-700 italic mt-0.5">Note : {n.completion_note}</div>
                    )}
                  </div>
                  <span onClick={(e) => e.stopPropagation()}>
                    <AdminTaskStatusSelector
                      itemType="rl31_request"
                      itemId={n.id}
                      value={n.admin_task_status}
                      onChanged={fetchItems}
                    />
                  </span>
                  <button
                    type="button"
                    onClick={(e) => handleDelete(n, e)}
                    disabled={deletingId === n.id}
                    className="text-rose-500 hover:text-rose-700 hover:bg-rose-50 p-1.5 rounded disabled:opacity-50"
                    data-testid={`admin-rl31-delete-${n.id}`}
                    title="Supprimer définitivement"
                  >
                    {deletingId === n.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
                  </button>
                  <span onClick={(e) => e.stopPropagation()}>
                    <ArchiveButton
                      itemType="rl31_request"
                      itemId={n.id}
                      itemLabel="cette demande RL-31"
                      onArchived={fetchItems}
                      size="xs"
                    />
                  </span>
                  <ChevronRight className="w-4 h-4 text-slate-300 flex-shrink-0" />
                </div>
              );
            })}
          </div>
        )}
      </div>
      {selected && (
        <Rl31DetailModal
          item={selected}
          onClose={() => setSelected(null)}
          onUpdated={() => { setSelected(null); fetchItems(); onUpdate?.(); }}
        />
      )}
    </>
  );
}
